import { RegexTable } from './regex-table'

/**
 * 数字の並びを切り出す正規表現
 *
 * `String.prototype.split` に渡して数字の並びとそれ以外に分けるため、捕捉グループを1つだけ持つ。
 */
export const NUMERAL_RUN = /([0-9０-９]+|[〇一二三四五六七八九十]+)/

const KANJI_DIGITS = ['〇', '一', '二', '三', '四', '五', '六', '七', '八', '九']

/** 丁目にも番地にも出ない桁数は数として読まない */
const MAX_DIGITS = 9

/** 半角と全角の対応は `RegexTable` が持っているので、1桁ずつ引いて文字クラスに変える */
function toWidthClasses(digits: string): string {
  return digits.replace(/[0-9０-９]/g, (c) => RegexTable[c] || c)
}

function parseArabic(run: string): number | null {
  if (run.length > MAX_DIGITS) {
    return null
  }

  const halfWidth = run.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
  return Number(halfWidth)
}

function parseKanji(run: string): number | null {
  if (!run.includes('十')) {
    // 十 を使わない並びは1桁ずつ読む（一 → 1、一〇 → 10）
    if (run.length > MAX_DIGITS) {
      return null
    }

    let value = 0
    for (const c of run) {
      const digit = KANJI_DIGITS.indexOf(c)
      if (digit < 0) {
        return null
      }
      value = value * 10 + digit
    }
    return value
  }

  // 十 を含む並びは2桁まで（十 → 10、二十 → 20、二十一 → 21）
  const parts = run.split('十')
  if (parts.length !== 2) {
    return null
  }

  const tens = parts[0] === '' ? 1 : KANJI_DIGITS.indexOf(parts[0])
  const ones = parts[1] === '' ? 0 : KANJI_DIGITS.indexOf(parts[1])
  if (tens < 1 || ones < 0) {
    return null
  }
  return tens * 10 + ones
}

/** 0 から 99 までを漢数字にする。範囲の外は漢数字の書き方が1つに決まらないので `null` を返す */
function toKanji(value: number): string | null {
  if (value < 0 || value > 99) {
    return null
  }
  if (value < 10) {
    return KANJI_DIGITS[value]
  }

  const tens = Math.floor(value / 10)
  const ones = value % 10
  return `${tens === 1 ? '' : KANJI_DIGITS[tens]}十${ones === 0 ? '' : KANJI_DIGITS[ones]}`
}

/**
 * 数字の並びを、同じ数の別の書き方にも当たる正規表現の断片に変換する
 *
 * **Usage**
 *
 * ```ts
 * toNumeralRegexSource('2')    // '(?:[2２]|二)'
 * toNumeralRegexSource('10')   // '(?:[1１][0０]|十)'
 * toNumeralRegexSource('二十一') // '(?:二十一|[2２][1１])'
 * ```
 *
 * **Details**
 *
 * 数として読めない並びには `null` を返すので、呼び出し側で文字どおりに扱う。
 * 打たれたとおりの書き方は必ず残すため、`01` のように頭に 0 が付く並びも取りこぼさない。
 */
export function toNumeralRegexSource(run: string): string | null {
  const isArabic = /^[0-9０-９]+$/.test(run)
  const value = isArabic ? parseArabic(run) : parseKanji(run)
  if (value === null) {
    return null
  }

  const forms = new Set<string>([isArabic ? toWidthClasses(run) : run])
  forms.add(toWidthClasses(String(value)))

  const kanji = toKanji(value)
  if (kanji !== null) {
    forms.add(kanji)
  }

  const sources = [...forms]
  return sources.length === 1 ? sources[0] : `(?:${sources.join('|')})`
}
